import React, { useState, useEffect, useRef } from 'react';
import { Calendar, MapPin, Users, Clock, AlertCircle, CheckCircle, XCircle, X, FileText, Plus, Edit2, Trash2, Eye, MessageSquare, Crown, ChefHat, UtensilsCrossed, Baby, AlertTriangle, Send, TrendingUp, DollarSign, Truck, Pencil, Check, RotateCcw, ChevronDown } from 'lucide-react';
import { API_URL } from '../config';
import EventTimeline from './EventTimeline';
import EventChecklist from './EventChecklist';

// Widget de rentabilité par événement (Patron et Super Manager uniquement)
function RentabiliteWidget({ eventId, authToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editValues, setEditValues] = useState({ ca_ht: '', cout_rh: '', cout_extras: '', cout_vehicules: '' });

  const loadData = () => {
    setLoading(true);
    fetch(`${API_URL}/evenements/${eventId}/rentabilite`, {
      headers: { 'Authorization': `Bearer ${authToken}` }
    })
      .then(res => {
        if (!res.ok) throw new Error('Erreur chargement rentabilité');
        return res.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  };

  useEffect(() => { loadData(); }, [eventId, authToken]);

  const startEditing = () => {
    setEditValues({
      ca_ht: data.ca_ht_manuel ? String(data.ca_ht) : '',
      cout_rh: data.cout_rh_manuel ? String(data.cout_rh_permanent) : '',
      cout_extras: data.cout_extras_manuel ? String(data.cout_extras) : '',
      cout_vehicules: data.cout_vehicules_manuel ? String(data.cout_vehicules) : ''
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/evenements/${eventId}/rentabilite`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ca_ht_reel: editValues.ca_ht || null,
          cout_rh_reel: editValues.cout_rh || null,
          cout_extras_reel: editValues.cout_extras || null,
          cout_vehicules_reel: editValues.cout_vehicules || null
        })
      });
      if (!res.ok) throw new Error('Erreur sauvegarde');
      setEditing(false);
      loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="pt-3 border-t border-border-subtle">
      <div className="bg-elevated-1 rounded-lg p-4 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
        <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-2/3"></div>
      </div>
    </div>
  );

  if (error) return (
    <div className="pt-3 border-t border-border-subtle">
      <div className="bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 rounded-lg p-3 text-sm flex items-center gap-2">
        <AlertCircle className="w-4 h-4" />
        {error}
      </div>
    </div>
  );

  if (!data) return null;

  const fmt = (v) => (v != null ? Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—');
  const taux = data.taux_marge != null ? Number(data.taux_marge) : null;
  const barColor = taux == null ? 'bg-gray-300' : taux < 20 ? 'bg-red-500' : taux <= 40 ? 'bg-orange-500' : 'bg-green-500';
  const barWidth = taux == null ? 0 : Math.min(Math.max(taux, 0), 100);

  const EditableCell = ({ label, icon, value, estimatedValue, isManual, editKey, color = 'text-text-secondary', subtext }) => (
    <div className="bg-surface rounded-lg p-3 shadow-sm">
      <div className="text-xs text-text-muted mb-1">{label}</div>
      {editing ? (
        <div>
          <input
            type="number"
            step="0.01"
            min="0"
            value={editValues[editKey]}
            onChange={(e) => setEditValues(prev => ({ ...prev, [editKey]: e.target.value }))}
            placeholder={`Estimé: ${fmt(estimatedValue)}`}
            className="w-full px-2 py-1 text-sm border border-border-strong rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <div className="text-xs text-text-muted mt-1">Estimé : {fmt(estimatedValue)} €</div>
        </div>
      ) : (
        <div>
          <div className={`text-sm font-bold ${color} flex items-center gap-1`}>
            {icon}
            {fmt(value)} €
          </div>
          {isManual && <div className="text-xs text-indigo-500 mt-0.5">valeur saisie</div>}
          {!isManual && <div className="text-xs text-text-muted mt-0.5">estimé</div>}
          {subtext && <div className="text-xs text-text-muted">{subtext}</div>}
        </div>
      )}
    </div>
  );

  return (
    <div className="pt-3 border-t border-border-subtle">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-text-secondary flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            Rentabilité de l'événement
          </h4>
          {!editing ? (
            <button onClick={startEditing} className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:text-indigo-200 transition">
              <Pencil className="w-3 h-3" /> Modifier
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={() => setEditing(false)} className="flex items-center gap-1 text-xs text-text-muted hover:text-text-secondary transition">
                <X className="w-3 h-3" /> Annuler
              </button>
              <button onClick={handleSave} disabled={saving} className="flex items-center gap-1 text-xs bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700 transition disabled:opacity-50">
                <Check className="w-3 h-3" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <EditableCell
            label="CA HT" icon={<DollarSign className="w-3 h-3" />}
            value={data.ca_ht} estimatedValue={data.ca_ht_estime}
            isManual={data.ca_ht_manuel} editKey="ca_ht" color="text-indigo-700 dark:text-indigo-300"
          />
          <EditableCell
            label="Coût RH permanent"
            value={data.cout_rh_permanent} estimatedValue={data.cout_rh_estime}
            isManual={data.cout_rh_manuel} editKey="cout_rh"
            subtext={!editing && data.heures_total != null ? `${Number(data.heures_total).toLocaleString('fr-FR')}h` : null}
          />
          <EditableCell
            label="Coût Extras"
            value={data.cout_extras} estimatedValue={data.cout_extras_estime}
            isManual={data.cout_extras_manuel} editKey="cout_extras"
            subtext={!editing && data.nb_extras != null ? `${data.nb_extras} extra${data.nb_extras > 1 ? 's' : ''}` : null}
          />
          <EditableCell
            label="Coût véhicules" icon={<Truck className="w-3 h-3" />}
            value={data.cout_vehicules} estimatedValue={data.cout_vehicules_estime}
            isManual={data.cout_vehicules_manuel} editKey="cout_vehicules"
            subtext={!editing && data.nb_vehicules != null ? `${data.nb_vehicules} véhicule${data.nb_vehicules > 1 ? 's' : ''}` : null}
          />
        </div>

        {!editing && (
          <>
            {/* Totaux et marge */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mb-3">
              <div className="bg-surface rounded-lg p-3 shadow-sm">
                <div className="text-xs text-text-muted mb-1">Total coûts</div>
                <div className="text-sm font-bold text-red-600 dark:text-red-400">{fmt(data.cout_total)} €</div>
              </div>
              <div className="bg-surface rounded-lg p-3 shadow-sm">
                <div className="text-xs text-text-muted mb-1">Marge brute</div>
                <div className={`text-sm font-bold ${data.marge_brute != null && Number(data.marge_brute) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {fmt(data.marge_brute)} €
                </div>
              </div>
              <div className="bg-surface rounded-lg p-3 shadow-sm">
                <div className="text-xs text-text-muted mb-1">Taux de marge</div>
                <div className={`text-sm font-bold ${taux != null && taux >= 40 ? 'text-green-600 dark:text-green-400' : taux != null && taux >= 20 ? 'text-orange-600 dark:text-orange-400' : 'text-red-600 dark:text-red-400'}`}>
                  {taux != null ? `${taux.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` : '—'}
                </div>
              </div>
            </div>

            {/* Barre de progression taux de marge */}
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
              <div className={`h-2.5 rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${barWidth}%` }}></div>
            </div>

            {/* Alerte pointages incomplets */}
            {data.pointages_incomplets != null && data.pointages_incomplets > 0 && (
              <div className="flex items-center gap-2 mt-2 text-xs text-amber-700 bg-amber-50 dark:bg-amber-950/40 rounded-lg px-3 py-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                {data.pointages_incomplets} pointage{data.pointages_incomplets > 1 ? 's' : ''} incomplet{data.pointages_incomplets > 1 ? 's' : ''} — les coûts RH peuvent être sous-estimés
              </div>
            )}
          </>
        )}

        {editing && (
          <div className="text-xs text-text-muted bg-surface rounded-lg p-2 mt-1">
            Laissez un champ vide pour utiliser la valeur estimée automatiquement.
          </div>
        )}
      </div>
    </div>
  );
}

export default function Evenements({ authUser, authToken }) {
  const [evenements, setEvenements] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vehicules, setVehicules] = useState([]);
  const [selectedVehicules, setSelectedVehicules] = useState([]);
  const [tempsLogistique, setTempsLogistique] = useState({
    chargement: 60,
    dechargement: 45,
    nettoyage: 30,
    trajet: 30
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAbsenceModal, setShowAbsenceModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showAbsencesListModal, setShowAbsencesListModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventAbsences, setEventAbsences] = useState([]);
  const [absenceForm, setAbsenceForm] = useState({ motif: '', fichier_justificatif: '' });
  const [eventForm, setEventForm] = useState({
    nom: '',
    date_debut: '',
    date_fin: '',
    lieu: '',
    description: '',
    nombre_invites: '',
    nombre_enfants: 0,
    forfait_extra: '',
    statut: 'en_attente',
    fichier_devis: '',
    chef_projet_id: '',
    besoins_cuisiniers: '',
    besoins_serveurs: '',
    participants: [],
    participantsDetails: [],
    vehicules: [],
    send_extra_invites: false,
    invite_mode: 'available',
    show_rentabilite: true
  });
  const [availabilityConflicts, setAvailabilityConflicts] = useState([]);
  const [staffingAlerts, setStaffingAlerts] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Chat par événement
  const [openChatId, setOpenChatId] = useState(null);
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [chatMessages, setChatMessages] = useState({});
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const chatEndRef = useRef(null);

  // Confirmations de présence
  const [myInvitations, setMyInvitations] = useState({}); // Extra: { [evenement_id]: invitation }
  const [extraInvitations, setExtraInvitations] = useState({}); // Admin: { [evenement_id]: [invitations] }
  const [confirmLoading, setConfirmLoading] = useState({});

  const isAdmin = authUser && ['Patron', 'Super Manager'].includes(authUser.role_name);

  useEffect(() => {
    if (authUser && authToken) {
      loadEvenements();
      if (isAdmin) {
        loadEmployees();
      }
      if (authUser.role_name === 'Extra') {
        loadMyInvitations();
      }
    }
  }, [authUser, authToken]);

  // Auto-calculer les besoins en staff quand le nombre d'invités change
  useEffect(() => {
    const totalInvites = (parseInt(eventForm.nombre_invites) || 0) + (parseInt(eventForm.nombre_enfants) || 0);
    if (totalInvites > 0) {
      const cuisiniers = Math.max(1, Math.ceil(totalInvites / 50));
      const serveurs = Math.max(1, Math.ceil(totalInvites / 25));
      setEventForm(prev => ({
        ...prev,
        besoins_cuisiniers: cuisiniers,
        besoins_serveurs: serveurs
      }));
    }
  }, [eventForm.nombre_invites, eventForm.nombre_enfants]);

  // Charger les véhicules disponibles quand les dates changent
  useEffect(() => {
    if (eventForm.date_debut && eventForm.date_fin && isAdmin) {
      loadVehiculesDisponibles(eventForm.date_debut, eventForm.date_fin, editingEvent?.id);
    }
  }, [eventForm.date_debut, eventForm.date_fin, editingEvent?.id]);

  // Polling messages du chat ouvert
  useEffect(() => {
    if (!openChatId) return;
    const evt = evenements.find(e => e.id === openChatId);
    if (!evt?.canal_messagerie_id) return;

    const fetchChat = async () => {
      try {
        const res = await fetch(`${API_URL}/canaux/${evt.canal_messagerie_id}/messages`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (res.ok) {
          const data = await res.json();
          setChatMessages(prev => ({ ...prev, [openChatId]: data }));
        }
      } catch {}
    };

    fetchChat();
    const interval = setInterval(fetchChat, 5000);
    return () => clearInterval(interval);
  }, [openChatId, authToken]);

  // Auto-scroll vers le bas quand de nouveaux messages arrivent
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, openChatId]);

  const sendChatMessage = async (e, evt) => {
    e.preventDefault();
    if (!chatInput.trim() || !evt.canal_messagerie_id || chatSending) return;
    setChatSending(true);
    try {
      const res = await fetch(`${API_URL}/canaux/${evt.canal_messagerie_id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ message: chatInput.trim() })
      });
      if (res.ok) {
        setChatInput('');
        const data = await res.json();
        setChatMessages(prev => ({
          ...prev,
          [evt.id]: [...(prev[evt.id] || []), { ...data, sender_name: `${authUser.first_name || ''} ${authUser.last_name || ''}`.trim() || 'Moi' }]
        }));
      }
    } catch {}
    setChatSending(false);
  };

  const loadMyInvitations = async () => {
    try {
      const res = await fetch(`${API_URL}/extra/my-invitations`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        const byEvent = {};
        data.forEach(inv => { byEvent[inv.evenement_id] = inv; });
        setMyInvitations(byEvent);
      }
    } catch {}
  };

  const loadEventExtraInvitations = async (eventId) => {
    try {
      const res = await fetch(`${API_URL}/evenements/${eventId}/extra-invitations`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setExtraInvitations(prev => ({ ...prev, [eventId]: data }));
      }
    } catch {}
  };

  const respondToInvitation = async (invitationId, status, eventId) => {
    setConfirmLoading(prev => ({ ...prev, [eventId]: true }));
    try {
      const res = await fetch(`${API_URL}/extra-invitations/${invitationId}/respond`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ status })
      });
      if (res.ok) {
        setMyInvitations(prev => ({
          ...prev,
          [eventId]: { ...prev[eventId], status }
        }));
        if (status === 'accepted') {
          loadEvenements();
        }
      }
    } catch {}
    setConfirmLoading(prev => ({ ...prev, [eventId]: false }));
  };

  const loadEmployees = async () => {
    try {
      const response = await fetch(`${API_URL}/employees`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (err) {
      console.error('Erreur chargement employés:', err);
    }
  };

  const loadVehiculesDisponibles = async (date_debut, date_fin, evenement_id = null) => {
    if (!date_debut || !date_fin) return;
    
    try {
      const params = new URLSearchParams({
        date_debut,
        date_fin,
        ...(evenement_id && { evenement_id })
      });
      
      const response = await fetch(`${API_URL}/vehicules/disponibles?${params}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVehicules(data);
      }
    } catch (err) {
      console.error('Erreur chargement véhicules:', err);
    }
  };

  const loadEvenements = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/evenements`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Si l'utilisateur est un employé, filtrer pour ne montrer que ses événements
        if (authUser && !['Patron', 'Super Manager', 'Manager'].includes(authUser.role_name)) {
          const mesEvenements = data.filter(evt => 
            evt.participants && evt.participants.some(p => p.employee_id === authUser.employee_id)
          );
          setEvenements(mesEvenements);
        } else {
          // Les managers/patrons voient tous les événements
          setEvenements(data);
        }
      } else {
        setError('Erreur lors du chargement des événements');
      }
    } catch (err) {
      console.error('Erreur chargement événements:', err);
      setError('Impossible de charger les événements');
    }
    setLoading(false);
  };

  const loadEventAbsences = async (eventId) => {
    try {
      const response = await fetch(`${API_URL}/evenements/${eventId}/absences`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEventAbsences(data);
      }
    } catch (err) {
      console.error('Erreur chargement absences:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  // Générer le message WhatsApp pour un événement
  const generateWhatsAppMessage = (evt) => {
    let msg = `🎉 *${evt.nom}*\n`;
    msg += `📅 ${formatDate(evt.date_debut)}\n`;
    msg += `⏰ ${formatTime(evt.date_debut)} - ${formatTime(evt.date_fin)}\n`;
    if (evt.lieu) msg += `📍 ${evt.lieu}\n`;
    if (evt.nombre_invites) {
      const total = (parseInt(evt.nombre_invites) || 0) + (parseInt(evt.nombre_enfants) || 0);
      msg += `👥 ${total} invités`;
      if (evt.nombre_enfants > 0) msg += ` (${evt.nombre_invites} adultes + ${evt.nombre_enfants} enfants)`;
      msg += `\n`;
    }
    if (evt.besoins_cuisiniers || evt.besoins_serveurs) {
      msg += `\n👨‍🍳 Effectifs requis :\n`;
      if (evt.besoins_cuisiniers > 0) msg += `  - ${evt.besoins_cuisiniers} cuisinier(s)\n`;
      if (evt.besoins_serveurs > 0) msg += `  - ${evt.besoins_serveurs} serveur(s)\n`;
    }
    if (evt.participants?.length > 0) {
      msg += `\n👥 Équipe :\n`;
      evt.participants.forEach(p => {
        const isChef = p.est_chef_projet || p.employee_id === evt.chef_projet_id;
        msg += `  ${isChef ? '👑 ' : '- '}${p.first_name} ${p.last_name}\n`;
      });
    }
    if (evt.description) msg += `\n📝 ${evt.description}\n`;
    msg += `\n_Envoyé depuis Staff Manager Pro - Best Events_`;
    return msg;
  };

  // Partager un événement sur WhatsApp (individuel)
  const shareEventWhatsApp = (evt, phone = null) => {
    const msg = generateWhatsAppMessage(evt);
    const url = phone
      ? `https://wa.me/${phone.replace(/[^0-9]/g, '').replace(/^0/, '33')}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  // Partager un événement à toute l'équipe sur WhatsApp
  const shareEventWhatsAppTeam = (evt) => {
    const msg = generateWhatsAppMessage(evt);
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const formatDateTimeForInput = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toISOString().slice(0, 16);
  };

  const getStatutBadge = (statut) => {
    const config = {
      'en_attente': { color: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200', icon: <AlertCircle className="w-4 h-4" />, label: 'En attente' },
      'confirme': { color: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200', icon: <CheckCircle className="w-4 h-4" />, label: 'Confirmé' },
      'annule': { color: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200', icon: <XCircle className="w-4 h-4" />, label: 'Annulé' }
    };
    const s = config[statut] || config['en_attente'];
    
    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold ${s.color}`}>
        {s.icon}
        {s.label}
      </span>
    );
  };

  // === VÉRIFICATIONS DISPONIBILITÉ ET EFFECTIFS ===

  const checkAvailability = async (participants, date_debut, date_fin, excludeEventId = null) => {
    if (!participants || participants.length === 0 || !date_debut) {
      setAvailabilityConflicts([]);
      return;
    }

    setCheckingAvailability(true);
    try {
      const response = await fetch(`${API_URL}/evenements/check-availability`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          participants: participants.map(p => ({ employee_id: p.employee_id || p })),
          date_debut,
          date_fin,
          exclude_event_id: excludeEventId
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAvailabilityConflicts(data.conflicts || []);
      }
    } catch (err) {
      console.error('Erreur vérification disponibilité:', err);
    }
    setCheckingAvailability(false);
  };

  const checkStaffing = async (nombre_invites, nombre_enfants, besoins_cuisiniers, besoins_serveurs, participants) => {
    try {
      const response = await fetch(`${API_URL}/evenements/check-staffing`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nombre_invites: parseInt(nombre_invites) || 0,
          nombre_enfants: parseInt(nombre_enfants) || 0,
          besoins_cuisiniers: parseInt(besoins_cuisiniers) || 0,
          besoins_serveurs: parseInt(besoins_serveurs) || 0,
          participants: participants || []
        })
      });

      if (response.ok) {
        const data = await response.json();
        setStaffingAlerts(data);
      }
    } catch (err) {
      console.error('Erreur vérification effectifs:', err);
    }
  };

  // Vérifier automatiquement quand les participants ou la date changent
  useEffect(() => {
    if (eventForm.date_debut && eventForm.participantsDetails.length > 0) {
      checkAvailability(
        eventForm.participantsDetails,
        eventForm.date_debut,
        eventForm.date_fin,
        editingEvent?.id
      );
    }
  }, [eventForm.date_debut, eventForm.date_fin, eventForm.participantsDetails]);

  // Vérifier automatiquement les effectifs
  useEffect(() => {
    if (eventForm.nombre_invites || eventForm.nombre_enfants) {
      checkStaffing(
        eventForm.nombre_invites,
        eventForm.nombre_enfants,
        eventForm.besoins_cuisiniers,
        eventForm.besoins_serveurs,
        eventForm.participantsDetails
      );
    }
  }, [
    eventForm.nombre_invites,
    eventForm.nombre_enfants,
    eventForm.besoins_cuisiniers,
    eventForm.besoins_serveurs,
    eventForm.participantsDetails
  ]);

  // === GESTION ÉVÉNEMENTS (ADMIN) ===

  const handleOpenEventModal = (event = null) => {
    setAvailabilityConflicts([]);
    setStaffingAlerts(null);

    if (event) {
      setEditingEvent(event);
      const participantsDetails = event.participants?.map(p => ({
        employee_id: p.employee_id,
        role_evenement: p.role_evenement,
        est_chef_projet: p.est_chef_projet || (p.employee_id === event.chef_projet_id),
        first_name: p.first_name,
        last_name: p.last_name
      })) || [];

      // Pré-remplir les véhicules déjà assignés à cet événement
      if (event.vehicules && event.vehicules.length > 0) {
        setSelectedVehicules(event.vehicules);
        const first = event.vehicules[0];
        if (first.temps_chargement_minutes || first.temps_dechargement_minutes || first.temps_nettoyage_minutes) {
          setTempsLogistique({
            chargement: first.temps_chargement_minutes || 60,
            dechargement: first.temps_dechargement_minutes || 45,
            nettoyage: first.temps_nettoyage_minutes || 30
          });
        }
      } else {
        setSelectedVehicules([]);
      }

      setEventForm({
        nom: event.nom,
        date_debut: formatDateTimeForInput(event.date_debut),
        date_fin: formatDateTimeForInput(event.date_fin),
        lieu: event.lieu,
        description: event.description || '',
        nombre_invites: event.nombre_invites || '',
        nombre_enfants: event.nombre_enfants || 0,
        forfait_extra: event.forfait_extra || '',
        statut: event.statut,
        fichier_devis: event.fichier_devis || '',
        chef_projet_id: event.chef_projet_id || '',
        besoins_cuisiniers: event.besoins_cuisiniers || '',
        besoins_serveurs: event.besoins_serveurs || '',
        participants: event.participants?.map(p => p.employee_id) || [],
        participantsDetails,
        send_extra_invites: false,
        invite_mode: event.invite_mode || 'available',
        show_rentabilite: event.show_rentabilite !== 0
      });
    } else {
      setEditingEvent(null);
      setEventForm({
        nom: '',
        date_debut: '',
        date_fin: '',
        lieu: '',
        description: '',
        nombre_invites: '',
        nombre_enfants: 0,
        forfait_extra: '',
        statut: 'en_attente',
        fichier_devis: '',
        chef_projet_id: '',
        besoins_cuisiniers: '',
        besoins_serveurs: '',
        participants: [],
        participantsDetails: [],
        send_extra_invites: false,
        invite_mode: 'available',
        show_rentabilite: true
      });
    }
    setShowEventModal(true);
  };

  const handleCloseEventModal = () => {
    setShowEventModal(false);
    setEditingEvent(null);
    setSelectedFile(null);
    setAvailabilityConflicts([]);
    setStaffingAlerts(null);
    setSelectedVehicules([]);
    setEventForm({
      nom: '',
      date_debut: '',
      date_fin: '',
      lieu: '',
      description: '',
      nombre_invites: '',
      nombre_enfants: 0,
      forfait_extra: '',
      statut: 'en_attente',
      fichier_devis: '',
      chef_projet_id: '',
      besoins_cuisiniers: '',
      besoins_serveurs: '',
      participants: [],
      participantsDetails: [],
      send_extra_invites: false,
      invite_mode: 'available',
      show_rentabilite: true
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('❌ Seuls les fichiers PDF sont acceptés');
        e.target.value = null;
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert('❌ Le fichier ne doit pas dépasser 10 MB');
        e.target.value = null;
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadFile = async () => {
    if (!selectedFile) {
      alert('Veuillez sélectionner un fichier');
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('devis', selectedFile);

      const response = await fetch(`${API_URL}/upload/devis`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setEventForm({ ...eventForm, fichier_devis: data.url });
        setSelectedFile(null);
        alert('✅ Fichier uploadé avec succès');
      } else {
        const error = await response.json();
        alert('❌ Erreur: ' + (error.error || 'Impossible d\'uploader le fichier'));
      }
    } catch (err) {
      console.error('Erreur upload:', err);
      alert('❌ ' + (err?.message || 'Erreur lors de l\'upload du fichier'));
    }
    setUploadingFile(false);
  };

  const handleSubmitEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.nom || !eventForm.date_debut || !eventForm.date_fin || !eventForm.lieu) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      const url = editingEvent 
        ? `${API_URL}/evenements/${editingEvent.id}`
        : `${API_URL}/evenements`;
      
      const method = editingEvent ? 'PUT' : 'POST';

      // Préparer les données
      const eventData = {
        nom: eventForm.nom,
        lieu: eventForm.lieu,
        date_debut: eventForm.date_debut,
        date_fin: eventForm.date_fin,
        nombre_invites: parseInt(eventForm.nombre_invites) || 0,
        nombre_enfants: parseInt(eventForm.nombre_enfants) || 0,
        forfait_extra: eventForm.forfait_extra,
        description: eventForm.description,
        fichier_devis: eventForm.fichier_devis,
        statut: eventForm.statut,
        chef_projet_id: eventForm.chef_projet_id ? parseInt(eventForm.chef_projet_id) : null,
        besoins_cuisiniers: parseInt(eventForm.besoins_cuisiniers) || 0,
        besoins_serveurs: parseInt(eventForm.besoins_serveurs) || 0,
        participants: eventForm.participants.map(employeeId => ({
          employee_id: employeeId,
          role_evenement: null,
          est_chef_projet: employeeId === parseInt(eventForm.chef_projet_id) ? 1 : 0
        })),
        vehicules: selectedVehicules.map(v => v.id),
        temps_chargement: tempsLogistique.chargement,
        temps_dechargement: tempsLogistique.dechargement,
        temps_nettoyage: tempsLogistique.nettoyage,
        temps_trajet_aller: tempsLogistique.trajet,
        send_extra_invites: eventForm.send_extra_invites,
        invite_mode: eventForm.invite_mode,
        show_rentabilite: eventForm.show_rentabilite
      };
      
      // Ajouter created_by pour la création
      if (!editingEvent) {
        eventData.created_by = authUser.employee_id;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventData)
      });

      if (response.ok) {
        const result = await response.json();
        
        // Stocker les warnings pour affichage
        if (result.conflicts && result.conflicts.length > 0) {
          setAvailabilityConflicts(result.conflicts);
        }
        
        if (result.staffing) {
          setStaffingAlerts(result.staffing);
        }
        
        // Afficher les avertissements s'il y en a
        let alertMessage = editingEvent ? '✅ Événement modifié avec succès' : '✅ Événement créé avec succès';
        
        if (result.warnings) {
          alertMessage += '\n\n⚠️ Avertissements :';
          
          if (result.conflicts && result.conflicts.length > 0) {
            alertMessage += '\n\n📅 Conflits de disponibilité :';
            result.conflicts.forEach(c => {
              alertMessage += `\n- ${c.first_name} ${c.last_name} est déjà sur "${c.conflit_evenement_nom}"`;
            });
          }
          
          if (result.staffing && result.staffing.alerts && result.staffing.alerts.length > 0) {
            alertMessage += '\n\n👥 Effectifs insuffisants :';
            result.staffing.alerts.forEach(a => {
              alertMessage += `\n- ${a.message}`;
            });
          }
        }
        
        if (result.canal_id) {
          alertMessage += `\n\n💬 Un canal de messagerie dédié a été créé pour cet événement`;
        }
        
        alert(alertMessage);
        
        // Ne fermer le modal que si pas de warnings critiques
        if (!result.warnings || window.confirm('Voulez-vous continuer malgré ces avertissements ?')) {
          handleCloseEventModal();
          loadEvenements();
        }
      } else {
        const data = await response.json();
        alert('❌ Erreur: ' + (data.error || 'Impossible de sauvegarder l\'événement'));
      }
    } catch (err) {
      console.error('Erreur sauvegarde événement:', err);
      alert('❌ Erreur lors de la sauvegarde de l\'événement');
    }
    setSubmitting(false);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/evenements/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        alert('✅ Événement supprimé avec succès');
        loadEvenements();
      } else {
        const data = await response.json();
        alert('❌ Erreur: ' + (data.error || 'Impossible de supprimer l\'événement'));
      }
    } catch (err) {
      console.error('Erreur suppression événement:', err);
      alert('❌ Erreur lors de la suppression de l\'événement');
    }
  };

  const handleToggleParticipant = (employeeId) => {
    const isSelected = eventForm.participants.includes(employeeId);
    const employee = employees.find(e => e.id === employeeId);
    
    if (!employee) return;
    
    if (isSelected) {
      // Retirer le participant
      setEventForm(prev => ({
        ...prev,
        participants: prev.participants.filter(id => id !== employeeId),
        participantsDetails: prev.participantsDetails.filter(p => p.employee_id !== employeeId),
        chef_projet_id: prev.chef_projet_id === employeeId ? '' : prev.chef_projet_id
      }));
    } else {
      // Ajouter le participant
      setEventForm(prev => ({
        ...prev,
        participants: [...prev.participants, employeeId],
        participantsDetails: [
          ...prev.participantsDetails,
          {
            employee_id: employeeId,
            first_name: employee.first_name,
            last_name: employee.last_name,
            role_evenement: '',
            est_chef_projet: false
          }
        ]
      }));
    }
  };

  const handleUpdateParticipantRole = (employeeId, role) => {
    setEventForm(prev => ({
      ...prev,
      participantsDetails: prev.participantsDetails.map(p =>
        p.employee_id === employeeId ? { ...p, role_evenement: role } : p
      )
    }));
  };

  // === GESTION ABSENCES ===

  const handleOpenAbsencesListModal = async (event) => {
    setSelectedEvent(event);
    await loadEventAbsences(event.id);
    setShowAbsencesListModal(true);
  };

  const handleCloseAbsencesListModal = () => {
    setShowAbsencesListModal(false);
    setSelectedEvent(null);
    setEventAbsences([]);
  };

  const handleProcessAbsence = async (absenceId, statut, commentaire) => {
    try {
      const response = await fetch(`${API_URL}/evenements/absences/${absenceId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          statut,
          commentaire_admin: commentaire,
          traite_par: authUser.employee_id
        })
      });

      if (response.ok) {
        alert(`✅ Absence ${statut === 'accepte' ? 'acceptée' : 'refusée'}`);
        await loadEventAbsences(selectedEvent.id);
      } else {
        alert('❌ Erreur lors du traitement de l\'absence');
      }
    } catch (err) {
      console.error('Erreur traitement absence:', err);
      alert('❌ Erreur lors du traitement de l\'absence');
    }
  };

  const handleOpenAbsenceModal = (event) => {
    setSelectedEvent(event);
    setAbsenceForm({ motif: '', fichier_justificatif: '' });
    setShowAbsenceModal(true);
  };

  const handleCloseAbsenceModal = () => {
    setShowAbsenceModal(false);
    setSelectedEvent(null);
    setAbsenceForm({ motif: '', fichier_justificatif: '' });
  };

  const handleSubmitAbsence = async (e) => {
    e.preventDefault();
    if (!absenceForm.motif.trim()) {
      alert('Veuillez indiquer le motif de votre absence');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/evenements/${selectedEvent.id}/absences`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          employee_id: authUser.employee_id,
          motif: absenceForm.motif,
          fichier_justificatif: absenceForm.fichier_justificatif
        })
      });

      if (response.ok) {
        alert('✅ Absence signalée avec succès');
        handleCloseAbsenceModal();
        loadEvenements();
      } else {
        const data = await response.json();
        alert('❌ Erreur: ' + (data.error || 'Impossible de signaler l\'absence'));
      }
    } catch (err) {
      console.error('Erreur signalement absence:', err);
      alert('❌ Erreur lors du signalement de l\'absence');
    }
    setSubmitting(false);
  };

  if (!authUser) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-surface rounded-xl shadow-lg p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-text-primary flex items-center gap-2 sm:gap-3">
              <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600 dark:text-orange-400" />
              {isAdmin ? 'Événements' : 'Mes Événements'}
            </h2>
            <p className="text-text-secondary text-sm mt-1">
              {isAdmin
                ? `${evenements.length} événement(s) au total`
                : `${evenements.length} événement(s) auxquels vous participez`
              }
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={() => handleOpenEventModal()}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold transition shadow-md text-sm sm:text-base"
            >
              <Plus className="w-5 h-5" />
              Nouvel événement
            </button>
          )}
        </div>
      </div>

      {/* Contenu */}
      {loading ? (
        <div className="bg-surface rounded-xl shadow-lg p-12 text-center">
          <div className="animate-spin w-12 h-12 border-4 border-orange-500 dark:border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-text-secondary">Chargement des événements...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-600 dark:text-red-400 mx-auto mb-3" />
          <p className="text-red-800 dark:text-red-200 font-semibold">{error}</p>
        </div>
      ) : evenements.length === 0 ? (
        <div className="bg-surface rounded-xl shadow-lg p-12 text-center">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-xl font-semibold text-text-secondary mb-2">Aucun événement pour le moment</p>
          <p className="text-text-muted">
            {isAdmin 
              ? 'Cliquez sur "Nouvel événement" pour créer votre premier événement.'
              : 'Vous serez notifié lorsque vous serez assigné à un événement.'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {evenements.map(evt => (
            <div key={evt.id} className="bg-surface rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition">
              {/* Header carte — compact */}
              <div className="bg-gradient-to-r from-orange-500 to-red-600 p-3 sm:p-4 lg:p-5 text-white">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <h3 className="text-base sm:text-lg lg:text-xl font-bold leading-tight">{evt.nom}</h3>
                  {getStatutBadge(evt.statut)}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-orange-100 text-sm">
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" />
                    {evt.lieu}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDate(evt.date_debut)}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    {formatTime(evt.date_debut)} - {formatTime(evt.date_fin)}
                  </span>
                </div>
              </div>

              {/* Corps carte — résumé toujours visible */}
              <div className="p-3 sm:p-4 lg:p-5 space-y-3">
                {/* Ligne résumé : invités + staff requis */}
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  {(evt.nombre_invites || evt.nombre_enfants) ? (
                    <span className="flex items-center gap-1.5 text-text-secondary">
                      <Users className="w-4 h-4 text-orange-500" />
                      <span className="font-semibold">{(parseInt(evt.nombre_invites) || 0) + (parseInt(evt.nombre_enfants) || 0)}</span> invités
                    </span>
                  ) : null}
                  {evt.besoins_cuisiniers > 0 && (
                    <span className="text-purple-700 bg-purple-50 dark:bg-purple-950/40 px-2 py-0.5 rounded-full text-xs font-medium">👨‍🍳 {evt.besoins_cuisiniers}</span>
                  )}
                  {evt.besoins_serveurs > 0 && (
                    <span className="text-purple-700 bg-purple-50 dark:bg-purple-950/40 px-2 py-0.5 rounded-full text-xs font-medium">🍽️ {evt.besoins_serveurs}</span>
                  )}
                </div>

                {/* Description tronquée */}
                {evt.description && (
                  <p className="text-sm text-text-secondary line-clamp-2">{evt.description}</p>
                )}

                {/* Équipe — toujours visible, compact */}
                <div>
                  <p className="text-xs font-semibold text-text-muted mb-1.5">
                    Équipe ({evt.participants?.length || 0})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {evt.participants?.slice(0, 6).map(p => (
                      <span
                        key={p.employee_id}
                        className={`px-2 py-0.5 rounded-full text-xs ${
                          p.employee_id === authUser.employee_id
                            ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200 font-semibold border border-orange-300 dark:border-orange-700'
                            : 'bg-elevated-1 text-text-secondary'
                        } ${
                          p.est_chef_projet || p.employee_id === evt.chef_projet_id ? 'ring-1 ring-purple-400' : ''
                        }`}
                      >
                        {(p.est_chef_projet || p.employee_id === evt.chef_projet_id) && '👑 '}
                        {p.first_name} {p.last_name?.[0]}.
                        {p.employee_id === authUser.employee_id && ' (Vous)'}
                      </span>
                    ))}
                    {(evt.participants?.length || 0) > 6 && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-gray-200 text-text-muted">
                        +{evt.participants.length - 6}
                      </span>
                    )}
                  </div>
                </div>

                {/* Bouton déplier/replier */}
                <button
                  onClick={() => setExpandedEventId(expandedEventId === evt.id ? null : evt.id)}
                  className="w-full flex items-center justify-center gap-2 py-2 min-h-[44px] text-sm font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:bg-orange-950/40 rounded-lg transition"
                >
                  <ChevronDown className={`w-4 h-4 transition-transform ${expandedEventId === evt.id ? 'rotate-180' : ''}`} />
                  {expandedEventId === evt.id ? 'Voir moins' : 'Voir plus de détails'}
                </button>

                {/* === SECTION DÉPLIABLE === */}
                {expandedEventId === evt.id && (
                <div className="space-y-4 pt-2 border-t border-border-subtle">

                {/* Horaires logistiques */}
                {evt.temps_trajet_aller_minutes > 0 && evt.date_debut && (
                  <div className="bg-indigo-50 dark:bg-indigo-950/40 rounded-lg p-3 border border-indigo-200">
                    <p className="text-xs font-semibold text-indigo-800 mb-1.5">🕐 Planning logistique</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-indigo-700 dark:text-indigo-300">
                      {(() => {
                        const [h, m] = (evt.date_debut.split('T')[1] || '00:00').split(':').map(Number);
                        const trajet = evt.temps_trajet_aller_minutes || 30;
                        const charg = evt.temps_chargement_minutes || 60;
                        const fmtMin = (tot) => {
                          const n = ((tot % 1440) + 1440) % 1440;
                          return `${String(Math.floor(n/60)).padStart(2,'0')}h${String(n%60).padStart(2,'0')}`;
                        };
                        return (
                          <>
                            <span>📦 Arrivée dépôt</span><span className="font-semibold">{fmtMin(h*60+m - trajet - charg)}</span>
                            <span>🚗 Départ dépôt</span><span className="font-semibold">{fmtMin(h*60+m - trajet)}</span>
                            <span>🎉 Début événement</span><span className="font-semibold">{formatTime(evt.date_debut)}</span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Nombre d'invités détaillé */}
                {evt.nombre_enfants > 0 && (
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Users className="w-4 h-4 text-orange-500" />
                    {evt.nombre_invites || 0} adultes + {evt.nombre_enfants} enfants
                  </div>
                )}

                {/* Description complète */}
                {evt.description && (
                  <div className="pt-2 border-t border-border-subtle">
                    <p className="text-sm text-text-secondary italic whitespace-pre-line">{evt.description}</p>
                  </div>
                )}

                {/* Équipe complète */}
                {(evt.participants?.length || 0) > 6 && (
                  <div className="pt-2 border-t border-border-subtle">
                    <p className="text-xs font-semibold text-text-muted mb-1.5">Équipe complète</p>
                    <div className="flex flex-wrap gap-1.5">
                      {evt.participants?.map(p => (
                        <span
                          key={p.employee_id}
                          className={`px-2.5 py-1 rounded-full text-xs ${
                            p.employee_id === authUser.employee_id
                              ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200 font-semibold border border-orange-300 dark:border-orange-700'
                              : 'bg-elevated-1 text-text-secondary'
                          } ${
                            p.est_chef_projet || p.employee_id === evt.chef_projet_id ? 'ring-1 ring-purple-400' : ''
                          }`}
                        >
                          {(p.est_chef_projet || p.employee_id === evt.chef_projet_id) && '👑 '}
                          {p.first_name} {p.last_name}
                          {p.employee_id === authUser.employee_id && ' (Vous)'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Forfait extra - visible uniquement par Patron, Super Manager et Extra */}
                {evt.forfait_extra && (authUser.role_name === 'Patron' || authUser.role_name === 'Super Manager' || authUser.role_name === 'Extra') && (
                  <div className="bg-green-50 dark:bg-green-950/40 rounded-lg p-3 border border-green-200">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-green-800 dark:text-green-200">
                        💰 Forfait Extra
                      </p>
                      <p className="text-lg font-bold text-green-700">
                        {parseFloat(evt.forfait_extra).toFixed(2)} €
                      </p>
                    </div>
                    {authUser.role_name === 'Extra' && (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                        Montant perçu pour votre prestation
                      </p>
                    )}
                  </div>
                )}

                {/* Bannière confirmation Extra */}
                {authUser.role_name === 'Extra' && (() => {
                  const inv = myInvitations[evt.id];
                  if (!inv) return null;
                  if (inv.status === 'pending') {
                    return (
                      <div className="bg-orange-50 dark:bg-orange-950/40 rounded-lg p-3 border border-orange-200">
                        <p className="text-sm font-semibold text-orange-800 dark:text-orange-200 mb-2">📩 Invitation en attente de votre réponse</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => respondToInvitation(inv.id, 'accepted', evt.id)}
                            disabled={confirmLoading[evt.id]}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 min-h-[44px] bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Accepter
                          </button>
                          <button
                            onClick={() => respondToInvitation(inv.id, 'declined', evt.id)}
                            disabled={confirmLoading[evt.id]}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 min-h-[44px] bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition"
                          >
                            <XCircle className="w-4 h-4" />
                            Décliner
                          </button>
                        </div>
                      </div>
                    );
                  }
                  if (inv.status === 'accepted') {
                    return (
                      <div className="flex items-center gap-2 bg-green-50 dark:bg-green-950/40 rounded-lg p-3 border border-green-200">
                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                        <p className="text-sm font-semibold text-green-800 dark:text-green-200">Participation confirmée</p>
                      </div>
                    );
                  }
                  if (inv.status === 'declined') {
                    return (
                      <div className="flex items-center gap-2 bg-red-50 dark:bg-red-950/40 rounded-lg p-3 border border-red-200">
                        <XCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                        <p className="text-sm font-semibold text-red-800 dark:text-red-200">Invitation déclinée</p>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Devis PDF */}
                {evt.fichier_devis && (
                  <div className="pt-3 border-t border-border-subtle">
                    <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-950/40 rounded-lg p-3 border border-blue-200">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        <span className="text-sm font-semibold text-blue-800 dark:text-blue-200">Devis disponible</span>
                      </div>
                      <a
                        href={evt.fichier_devis}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition"
                      >
                        Voir PDF
                      </a>
                    </div>
                  </div>
                )}

                {/* Rentabilité de l'événement (Patron / Super Manager, si activé) */}
                {['Patron', 'Super Manager'].includes(authUser?.role_name) && evt.show_rentabilite !== 0 && (
                  <RentabiliteWidget eventId={evt.id} authToken={authToken} />
                )}

                {/* Déroulé Jour J (Timeline) */}
                {(evt.statut === 'planifie' || evt.statut === 'en_cours' || !evt.statut) && (
                  <div className="pt-3 border-t border-border-subtle">
                    <EventTimeline
                      eventId={evt.id}
                      authUser={authUser}
                      authToken={authToken}
                      employees={employees}
                      isChefProjet={evt.chef_projet_id === authUser?.id || evt.chef_projet_id === authUser?.employee_id}
                      readOnly={!['Patron', 'Super Manager'].includes(authUser?.role_name) && evt.chef_projet_id !== authUser?.id && evt.chef_projet_id !== authUser?.employee_id}
                    />
                  </div>
                )}

                {/* Checklist logistique */}
                {(evt.statut === 'planifie' || evt.statut === 'en_cours' || !evt.statut) && (
                  <div className="pt-3 border-t border-border-subtle">
                    <EventChecklist
                      eventId={evt.id}
                      authUser={authUser}
                      authToken={authToken}
                      readOnly={!['Patron', 'Super Manager'].includes(authUser?.role_name) && evt.chef_projet_id !== authUser?.id && evt.chef_projet_id !== authUser?.employee_id}
                    />
                  </div>
                )}

                {/* Chat de l'événement */}
                {/* Partage WhatsApp */}
                <div className="pt-3 border-t border-border-subtle">
                  <div className="flex gap-2">
                    <button
                      onClick={() => shareEventWhatsAppTeam(evt)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition text-sm"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      Partager sur WhatsApp
                    </button>
                  </div>
                </div>

                {evt.canal_messagerie_id && (
                  <div className="pt-3 border-t border-border-subtle">
                    {/* Bouton toggle */}
                    <button
                      onClick={() => setOpenChatId(openChatId === evt.id ? null : evt.id)}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] rounded-lg font-semibold transition text-sm ${
                        openChatId === evt.id
                          ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-700'
                          : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                      }`}
                    >
                      <MessageSquare className="w-4 h-4" />
                      Discussion équipe
                      {chatMessages[evt.id]?.length > 0 && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${openChatId === evt.id ? 'bg-indigo-200 dark:bg-indigo-800/40 text-indigo-700 dark:text-indigo-300' : 'bg-indigo-300 text-white'}`}>
                          {chatMessages[evt.id].length}
                        </span>
                      )}
                    </button>

                    {/* Panel de chat */}
                    {openChatId === evt.id && (
                      <div className="mt-2 border border-indigo-200 dark:border-indigo-800 rounded-xl overflow-hidden shadow-md">
                        {/* Header */}
                        <div className="bg-indigo-600 px-4 py-2.5 flex items-center justify-between">
                          <span className="text-white text-sm font-semibold flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" />
                            Discussion — {evt.nom}
                          </span>
                          <button onClick={() => setOpenChatId(null)} className="text-indigo-200 hover:text-white">
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Messages */}
                        <div className="bg-elevated-1 p-3 h-64 overflow-y-auto space-y-2">
                          {!(chatMessages[evt.id]?.length > 0) ? (
                            <div className="h-full flex items-center justify-center">
                              <p className="text-text-muted text-sm text-center">
                                Aucun message pour l'instant.<br />Soyez le premier à écrire !
                              </p>
                            </div>
                          ) : (
                            chatMessages[evt.id].map(msg => {
                              const isMine = msg.sender_id === authUser.employee_id;
                              return (
                                <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                                  <div className={`max-w-[78%] rounded-2xl px-3 py-2 ${
                                    isMine
                                      ? 'bg-indigo-500 text-white rounded-br-sm'
                                      : 'bg-surface border border-border-subtle text-text-primary rounded-bl-sm'
                                  }`}>
                                    {!isMine && (
                                      <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 mb-0.5">{msg.sender_name}</p>
                                    )}
                                    <p className="text-sm leading-snug">{msg.message}</p>
                                    <p className={`text-xs mt-1 ${isMine ? 'text-indigo-200' : 'text-text-muted'}`}>
                                      {new Date(msg.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                  </div>
                                </div>
                              );
                            })
                          )}
                          <div ref={chatEndRef} />
                        </div>

                        {/* Saisie */}
                        <form
                          onSubmit={e => sendChatMessage(e, evt)}
                          className="flex items-center gap-2 bg-surface border-t border-border-subtle px-3 py-2"
                        >
                          <input
                            type="text"
                            value={chatInput}
                            onChange={e => setChatInput(e.target.value)}
                            placeholder="Écrire un message…"
                            className="flex-1 text-base sm:text-sm border border-border-strong rounded-lg px-3 py-2 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            autoComplete="off"
                          />
                          <button
                            type="submit"
                            disabled={chatSending || !chatInput.trim()}
                            className="bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 text-white p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg transition"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        </form>
                      </div>
                    )}
                  </div>
                )}

                </div>
                )}
                {/* === FIN SECTION DÉPLIABLE === */}

                {/* Actions */}
                <div className="pt-4 border-t border-border-subtle space-y-3">
                  {isAdmin ? (
                    <>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleOpenEventModal(evt)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition text-sm"
                        >
                          <Edit2 className="w-4 h-4" />
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDeleteEvent(evt.id)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold transition text-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                          Supprimer
                        </button>
                      </div>
                      <button
                        onClick={() => handleOpenAbsencesListModal(evt)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold transition text-sm"
                      >
                        <Eye className="w-4 h-4" />
                        Voir les absences
                      </button>
                      {/* Confirmations Extras */}
                      {evt.send_extra_invites ? (
                        <div className="border border-indigo-200 dark:border-indigo-800 rounded-lg overflow-hidden">
                          <button
                            onClick={() => {
                              if (!extraInvitations[evt.id]) loadEventExtraInvitations(evt.id);
                              else setExtraInvitations(prev => { const n = {...prev}; delete n[evt.id]; return n; });
                            }}
                            className="w-full flex items-center justify-between px-4 py-2 min-h-[44px] bg-indigo-50 dark:bg-indigo-950/40 hover:bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200 font-semibold text-sm transition"
                          >
                            <span className="flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              Confirmations Extras
                            </span>
                            {extraInvitations[evt.id] && (
                              <span className="flex gap-2 text-xs">
                                <span className="text-green-700 dark:text-green-300">{extraInvitations[evt.id].filter(i => i.status === 'accepted').length} ✓</span>
                                <span className="text-red-600 dark:text-red-400">{extraInvitations[evt.id].filter(i => i.status === 'declined').length} ✗</span>
                                <span className="text-yellow-600 dark:text-yellow-400">{extraInvitations[evt.id].filter(i => i.status === 'pending').length} ⏳</span>
                              </span>
                            )}
                          </button>
                          {extraInvitations[evt.id] && (
                            <div className="bg-surface divide-y divide-gray-100 max-h-40 overflow-y-auto">
                              {extraInvitations[evt.id].length === 0 ? (
                                <p className="text-xs text-text-muted p-3 text-center">Aucune invitation envoyée</p>
                              ) : extraInvitations[evt.id].map(inv => (
                                <div key={inv.id} className="flex items-center justify-between px-3 py-1.5 text-sm">
                                  <span className="text-text-secondary">{inv.first_name} {inv.last_name}</span>
                                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                    inv.status === 'accepted' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' :
                                    inv.status === 'declined' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' :
                                    'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300'
                                  }`}>
                                    {inv.status === 'accepted' ? 'Confirmé' : inv.status === 'declined' ? 'Décliné' : 'En attente'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <button
                      onClick={() => handleOpenAbsenceModal(evt)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 min-h-[44px] bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold transition shadow-md text-sm"
                    >
                      <AlertCircle className="w-5 h-5" />
                      Signaler une absence
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Créer/Modifier Événement (Admin uniquement) */}
      {showEventModal && isAdmin && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="w-full sm:max-w-2xl sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface shadow-2xl">
            {/* Barre de drag mobile */}
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            {/* Header Modal */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-gradient-to-r from-orange-500 to-red-600 text-white shrink-0 sm:rounded-t-2xl">
              <h3 className="text-lg sm:text-2xl font-bold flex items-center gap-2 sm:gap-3">
                <Calendar className="w-5 h-5 sm:w-7 sm:h-7" />
                {editingEvent ? 'Modifier l\'événement' : 'Nouvel événement'}
              </h3>
              <button
                onClick={handleCloseEventModal}
                className="text-white hover:bg-white/20 p-2.5 rounded-lg transition min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Corps Modal */}
            <form onSubmit={handleSubmitEvent} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nom */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    Nom de l'événement <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={eventForm.nom}
                    onChange={(e) => setEventForm({ ...eventForm, nom: e.target.value })}
                    placeholder="Ex: Mariage Eve Gavas"
                    required
                    className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                {/* Date début */}
                <div>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    Date et heure de début <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={eventForm.date_debut}
                    onChange={(e) => setEventForm({ ...eventForm, date_debut: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                {/* Date fin */}
                <div>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    Date et heure de fin <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={eventForm.date_fin}
                    onChange={(e) => setEventForm({ ...eventForm, date_fin: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                {/* Lieu */}
                <div>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    Lieu <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={eventForm.lieu}
                    onChange={(e) => setEventForm({ ...eventForm, lieu: e.target.value })}
                    placeholder="Ex: Château de Versailles"
                    required
                    className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                {/* Nombre d'invités adultes */}
                <div>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    Nombre d'invités adultes
                  </label>
                  <input
                    type="number"
                    value={eventForm.nombre_invites}
                    onChange={(e) => setEventForm({ ...eventForm, nombre_invites: e.target.value })}
                    placeholder="Ex: 120"
                    min="0"
                    className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                {/* Nombre d'enfants */}
                <div>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    Nombre d'enfants
                  </label>
                  <input
                    type="number"
                    value={eventForm.nombre_enfants || ''}
                    onChange={(e) => setEventForm({ ...eventForm, nombre_enfants: e.target.value })}
                    placeholder="Ex: 30"
                    min="0"
                    className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <p className="text-xs text-text-muted mt-1">
                    Total invités : {(parseInt(eventForm.nombre_invites) || 0) + (parseInt(eventForm.nombre_enfants) || 0)}
                  </p>
                </div>

                {/* Statut */}
                <div>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    Statut <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={eventForm.statut}
                    onChange={(e) => setEventForm({ ...eventForm, statut: e.target.value })}
                    className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="en_attente">En attente</option>
                    <option value="confirme">Confirmé</option>
                    <option value="annule">Annulé</option>
                  </select>
                </div>

                {/* Cuisiniers nécessaires */}
                <div>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    👨‍🍳 Cuisiniers nécessaires
                  </label>
                  <input
                    type="number"
                    value={eventForm.besoins_cuisiniers || ''}
                    onChange={(e) => setEventForm({ ...eventForm, besoins_cuisiniers: e.target.value })}
                    placeholder="Auto-calculé"
                    min="0"
                    className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <p className="text-xs text-text-muted mt-1">
                    Recommandation : 1 cuisinier pour 50 invités
                  </p>
                </div>

                {/* Serveurs nécessaires */}
                <div>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    🍽️ Serveurs nécessaires
                  </label>
                  <input
                    type="number"
                    value={eventForm.besoins_serveurs || ''}
                    onChange={(e) => setEventForm({ ...eventForm, besoins_serveurs: e.target.value })}
                    placeholder="Auto-calculé"
                    min="0"
                    className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <p className="text-xs text-text-muted mt-1">
                    Recommandation : 1 serveur pour 20-30 invités (moyenne: 25)
                  </p>
                </div>

                {/* Forfait extra */}
                <div>
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    💰 Forfait Extra (en €)
                  </label>
                  <input
                    type="number"
                    value={eventForm.forfait_extra}
                    onChange={(e) => setEventForm({ ...eventForm, forfait_extra: e.target.value })}
                    placeholder="Ex: 150.00"
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <p className="text-xs text-text-muted mt-1">
                    Montant perçu par les employés Extra pour cette prestation (visible uniquement par les organisateurs et les Extra)
                  </p>
                </div>

                {/* Envoi invitations aux Extras */}
                {isAdmin && (
                  <div className="md:col-span-2 bg-orange-50 dark:bg-orange-950/40 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <input
                        type="checkbox"
                        id="send_extra_invites"
                        checked={eventForm.send_extra_invites}
                        onChange={(e) => setEventForm({ ...eventForm, send_extra_invites: e.target.checked })}
                        className="w-5 h-5 text-orange-500 rounded focus:ring-orange-500"
                      />
                      <label htmlFor="send_extra_invites" className="text-sm font-semibold text-text-secondary cursor-pointer">
                        📧 Envoyer les invitations aux Extras
                      </label>
                    </div>
                    
                    {eventForm.send_extra_invites && (
                      <div className="ml-8 space-y-2">
                        <p className="text-xs text-text-secondary mb-2">Choisir le mode d'envoi :</p>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="invite_mode"
                            value="available"
                            checked={eventForm.invite_mode === 'available'}
                            onChange={() => setEventForm({ ...eventForm, invite_mode: 'available' })}
                            className="text-orange-500 focus:ring-orange-500"
                          />
                          <span className="text-sm text-text-secondary">
                            <strong>Extras disponibles uniquement</strong>
                            <span className="text-text-muted text-xs ml-1">(pas de conflit de planning ce jour-là)</span>
                          </span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="invite_mode"
                            value="all"
                            checked={eventForm.invite_mode === 'all'}
                            onChange={() => setEventForm({ ...eventForm, invite_mode: 'all' })}
                            className="text-orange-500 focus:ring-orange-500"
                          />
                          <span className="text-sm text-text-secondary">
                            <strong>Tous les Extras</strong>
                            <span className="text-text-muted text-xs ml-1">(disponibles + indisponibles)</span>
                          </span>
                        </label>
                        {editingEvent && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                            Les extras déjà invités ne recevront pas de doublon.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Toggle Rentabilité */}
                {isAdmin && (
                  <div className="md:col-span-2 bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="show_rentabilite"
                        checked={eventForm.show_rentabilite}
                        onChange={(e) => setEventForm({ ...eventForm, show_rentabilite: e.target.checked })}
                        className="w-5 h-5 text-indigo-500 rounded focus:ring-indigo-500"
                      />
                      <label htmlFor="show_rentabilite" className="text-sm font-semibold text-text-secondary cursor-pointer">
                        <TrendingUp className="w-4 h-4 inline mr-1 text-indigo-600 dark:text-indigo-400" />
                        Afficher la rentabilité de l'événement
                      </label>
                    </div>
                    <p className="text-xs text-text-muted mt-1 ml-8">
                      Active le suivi de rentabilité (CA, coûts RH, extras, véhicules). Les valeurs sont éditables manuellement.
                    </p>
                  </div>
                )}

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    Description
                  </label>
                  <textarea
                    value={eventForm.description}
                    onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                    placeholder="Détails supplémentaires..."
                    rows={3}
                    className="w-full px-4 py-2 border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                  />
                </div>

                {/* Devis PDF */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    Devis PDF
                  </label>
                  
                  {/* Option 1: Upload depuis l'ordinateur */}
                  <div className="bg-elevated-1 rounded-lg p-4 border border-border-subtle mb-3">
                    <p className="text-sm font-semibold text-text-secondary mb-2">
                      📤 Importer depuis l'ordinateur
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={handleFileSelect}
                        className="flex-1 text-sm text-text-secondary file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 dark:bg-orange-950/40 file:text-orange-700 hover:file:bg-orange-100 dark:bg-orange-900/40"
                      />
                      {selectedFile && (
                        <button
                          type="button"
                          onClick={handleUploadFile}
                          disabled={uploadingFile}
                          className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {uploadingFile ? 'Upload...' : 'Uploader'}
                        </button>
                      )}
                    </div>
                    {selectedFile && (
                      <p className="text-xs text-text-secondary mt-2">
                        📄 Fichier sélectionné : {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </p>
                    )}
                  </div>

                  {/* Option 2: Saisir URL */}
                  <div className="bg-elevated-1 rounded-lg p-4 border border-border-subtle">
                    <p className="text-sm font-semibold text-text-secondary mb-2">
                      🔗 Ou saisir une URL
                    </p>
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-text-muted flex-shrink-0" />
                      <input
                        type="text"
                        value={eventForm.fichier_devis}
                        onChange={(e) => setEventForm({ ...eventForm, fichier_devis: e.target.value })}
                        placeholder="https://drive.google.com/file/d/xxx"
                        className="flex-1 px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>

                  {/* Prévisualisation */}
                  {eventForm.fichier_devis && (
                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          <span className="text-sm font-semibold text-blue-800 dark:text-blue-200">Devis enregistré</span>
                        </div>
                        <a
                          href={eventForm.fichier_devis}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold transition"
                        >
                          Voir PDF
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* Participants */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-text-secondary mb-2">
                    Participants
                  </label>
                  <div className="border border-border-strong rounded-lg p-4 max-h-48 overflow-y-auto">
                    {employees.length === 0 ? (
                      <p className="text-text-muted text-sm">Aucun employé disponible</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {employees.map(emp => (
                          <label key={emp.id} className="flex items-center gap-3 cursor-pointer hover:bg-elevated-1 p-2 rounded min-h-[44px]">
                            <input
                              type="checkbox"
                              checked={eventForm.participants.includes(emp.id)}
                              onChange={() => handleToggleParticipant(emp.id)}
                              className="w-4 h-4 text-orange-600 dark:text-orange-400 focus:ring-orange-500 border-border-strong rounded"
                            />
                            <span className="text-sm text-text-secondary">
                              {emp.first_name} {emp.last_name} 
                              <span className="text-text-muted"> ({emp.role_name})</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-text-muted mt-1">
                    {eventForm.participants.length} participant(s) sélectionné(s)
                  </p>
                </div>

                {/* Chef de projet */}
                {eventForm.participants.length > 0 && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-text-secondary mb-2">
                      👑 Chef de projet
                    </label>
                    <select
                      value={eventForm.chef_projet_id || ''}
                      onChange={(e) => setEventForm({ ...eventForm, chef_projet_id: e.target.value })}
                      className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">Aucun chef de projet</option>
                      {employees
                        .filter(emp => eventForm.participants.includes(emp.id))
                        .map(emp => (
                          <option key={emp.id} value={emp.id}>
                            {emp.first_name} {emp.last_name} ({emp.role_name})
                          </option>
                        ))}
                    </select>
                    <p className="text-xs text-text-muted mt-1">
                      Le chef de projet aura accès à toutes les informations de l'événement et au canal de messagerie dédié.
                    </p>
                  </div>
                )}

                {/* Véhicules */}
                {eventForm.date_debut && eventForm.date_fin && (
                  <div className="md:col-span-2">
                    <div className="border-t border-border-subtle pt-4">
                      <label className="block text-sm font-semibold text-text-secondary mb-2">
                        🚗 Véhicules nécessaires
                      </label>
                      
                      {vehicules.length === 0 ? (
                        <p className="text-text-muted text-sm italic p-3 bg-elevated-1 rounded-lg">
                          Aucun véhicule disponible pour ces dates. Vérifiez les dates sélectionnées ou ajoutez des véhicules dans le module Véhicules.
                        </p>
                      ) : (
                        <div className="border border-border-strong rounded-lg p-4 max-h-48 overflow-y-auto">
                          <div className="space-y-2">
                            {vehicules.map(vehicule => (
                              <label 
                                key={vehicule.id} 
                                className={`flex items-center justify-between gap-3 cursor-pointer hover:bg-elevated-1 p-3 rounded border ${
                                  selectedVehicules.find(v => v.id === vehicule.id) 
                                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/40' 
                                    : vehicule.disponible ? 'border-border-subtle' : 'border-red-200 bg-red-50 dark:bg-red-950/40 opacity-60'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <input
                                    type="checkbox"
                                    disabled={!vehicule.disponible}
                                    checked={selectedVehicules.find(v => v.id === vehicule.id) !== undefined}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedVehicules([...selectedVehicules, vehicule]);
                                      } else {
                                        setSelectedVehicules(selectedVehicules.filter(v => v.id !== vehicule.id));
                                      }
                                    }}
                                    className="w-4 h-4 text-orange-600 dark:text-orange-400 focus:ring-orange-500 border-border-strong rounded"
                                  />
                                  <div className="flex-1">
                                    <div className="font-medium text-sm text-text-secondary">
                                      {vehicule.nom} ({vehicule.immatriculation})
                                    </div>
                                    <div className="text-xs text-text-muted">
                                      {vehicule.type} · {vehicule.capacite} places
                                    </div>
                                    {!vehicule.disponible && vehicule.evenements_conflictuels && (
                                      <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                                        ⚠️ Déjà réservé : {vehicule.evenements_conflictuels}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded ${
                                  vehicule.disponible ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
                                }`}>
                                  {vehicule.disponible ? '✓ Disponible' : '✗ Indisponible'}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedVehicules.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <div>
                              <p className="text-sm font-medium text-blue-800">
                                {selectedVehicules.length} véhicule(s) sélectionné(s)
                              </p>
                              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                Total places disponibles : {selectedVehicules.reduce((sum, v) => sum + v.capacite, 0)}
                              </p>
                            </div>
                          </div>

                          {/* Alerte si pas assez de places */}
                          {eventForm.participants.length > 0 && 
                           selectedVehicules.reduce((sum, v) => sum + v.capacite, 0) < eventForm.participants.length && (
                            <div className="p-3 bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-300 rounded-lg">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                                <div className="flex-1">
                                  <p className="text-xs font-medium text-yellow-800">
                                    ⚠️ Places insuffisantes
                                  </p>
                                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                                    {selectedVehicules.reduce((sum, v) => sum + v.capacite, 0)} places disponibles 
                                    pour {eventForm.participants.length} participant(s). Il manque {' '}
                                    {eventForm.participants.length - selectedVehicules.reduce((sum, v) => sum + v.capacite, 0)} place(s).
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Temps logistique */}
                          <div className="p-3 bg-elevated-1 border border-border-subtle rounded-lg">
                            <p className="text-xs font-medium text-text-secondary mb-2">
                              ⏱️ Temps logistique (service Logistique)
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                              <div>
                                <label className="text-xs text-text-secondary block mb-1">🚗 Trajet aller (min)</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={tempsLogistique.trajet}
                                  onChange={(e) => setTempsLogistique({ ...tempsLogistique, trajet: parseInt(e.target.value) || 0 })}
                                  className="w-full px-2 py-1 text-xs border border-orange-300 rounded bg-orange-50 dark:bg-orange-950/40"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-text-secondary block mb-1">📦 Chargement (min)</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={tempsLogistique.chargement}
                                  onChange={(e) => setTempsLogistique({ ...tempsLogistique, chargement: parseInt(e.target.value) || 0 })}
                                  className="w-full px-2 py-1 text-xs border border-border-strong rounded"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-text-secondary block mb-1">Déchargement (min)</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={tempsLogistique.dechargement}
                                  onChange={(e) => setTempsLogistique({ ...tempsLogistique, dechargement: parseInt(e.target.value) || 0 })}
                                  className="w-full px-2 py-1 text-xs border border-border-strong rounded"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-text-secondary block mb-1">Nettoyage (min)</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={tempsLogistique.nettoyage}
                                  onChange={(e) => setTempsLogistique({ ...tempsLogistique, nettoyage: parseInt(e.target.value) || 0 })}
                                  className="w-full px-2 py-1 text-xs border border-border-strong rounded"
                                />
                              </div>
                            </div>
                            {eventForm.date_debut && (
                              <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 rounded text-xs text-blue-800 dark:text-blue-200">
                                <p className="font-semibold mb-1">📅 Horaires calculés :</p>
                                <p>• Arrivée dépôt / Chargement : {(() => {
                                  const [h, m] = (eventForm.date_debut.split('T')[1] || '00:00').split(':').map(Number);
                                  const tot = ((h * 60 + m - tempsLogistique.trajet - tempsLogistique.chargement) % 1440 + 1440) % 1440;
                                  return `${String(Math.floor(tot/60)).padStart(2,'0')}h${String(tot%60).padStart(2,'0')}`;
                                })()}</p>
                                <p>• Départ dépôt : {(() => {
                                  const [h, m] = (eventForm.date_debut.split('T')[1] || '00:00').split(':').map(Number);
                                  const tot = ((h * 60 + m - tempsLogistique.trajet) % 1440 + 1440) % 1440;
                                  return `${String(Math.floor(tot/60)).padStart(2,'0')}h${String(tot%60).padStart(2,'0')}`;
                                })()}</p>
                                <p>• Début événement : {(eventForm.date_debut.split('T')[1] || '').substring(0, 5).replace(':', 'h')}</p>
                              </div>
                            )}
                            <p className="text-xs text-text-muted mt-2">
                              Ces temps servent à calculer les horaires logistiques du briefing.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Alertes de disponibilité et staffing */}
              {(availabilityConflicts.length > 0 || (staffingAlerts && staffingAlerts.alerts && staffingAlerts.alerts.length > 0)) && (
                <div className="space-y-3 mt-4">
                  {/* Conflits de disponibilité */}
                  {availabilityConflicts.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-950/40 border-l-4 border-red-500 p-4 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-sm font-bold text-red-800 mb-2">
                            ⚠️ Conflits de disponibilité détectés
                          </h4>
                          <ul className="space-y-1 text-sm text-red-700 dark:text-red-300">
                            {availabilityConflicts.map((conflict, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-red-500">•</span>
                                <span>
                                  <strong>{conflict.first_name} {conflict.last_name}</strong> est déjà programmé(e) sur 
                                  <strong> "{conflict.conflit_evenement_nom}"</strong> 
                                  {conflict.conflit_date_debut && ` le ${formatDate(conflict.conflit_date_debut)}`}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Alertes d'effectifs */}
                  {staffingAlerts && staffingAlerts.alerts && staffingAlerts.alerts.length > 0 && (
                    <div className="bg-orange-50 dark:bg-orange-950/40 border-l-4 border-orange-500 p-4 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Users className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-sm font-bold text-orange-800 mb-2">
                            👥 Effectifs insuffisants
                          </h4>
                          <ul className="space-y-1 text-sm text-orange-700 dark:text-orange-300">
                            {staffingAlerts.alerts.map((alert, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-orange-500">•</span>
                                <span>{alert.message}</span>
                              </li>
                            ))}
                          </ul>
                          {staffingAlerts.recommandations && (
                            <div className="mt-3 pt-3 border-t border-orange-200">
                              <p className="text-xs font-semibold text-orange-800 mb-1">Recommandations :</p>
                              <p className="text-xs text-orange-700 dark:text-orange-300">
                                📊 Cuisiniers requis : {staffingAlerts.recommandations.cuisiniers} | 
                                Serveurs requis : {staffingAlerts.recommandations.serveurs}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

            </div>
            {/* Footer sticky avec boutons */}
            <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
                <button
                  type="button"
                  onClick={handleCloseEventModal}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center border-2 border-border-strong text-text-secondary hover:bg-elevated-1 transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-orange-600 hover:bg-orange-700 text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Enregistrement...' : editingEvent ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Liste des Absences (Admin uniquement) */}
      {showAbsencesListModal && isAdmin && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="w-full sm:max-w-2xl sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface shadow-2xl">
            {/* Barre de drag mobile */}
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            {/* Header Modal */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-gradient-to-r from-yellow-500 to-orange-600 text-white shrink-0 sm:rounded-t-2xl">
              <div>
                <h3 className="text-lg sm:text-2xl font-bold flex items-center gap-2 sm:gap-3">
                  <AlertCircle className="w-5 h-5 sm:w-7 sm:h-7" />
                  Demandes d'absence
                </h3>
                <p className="text-yellow-100 mt-1 text-xs sm:text-sm">
                  {selectedEvent.nom} - {formatDate(selectedEvent.date_debut)}
                </p>
              </div>
              <button
                onClick={handleCloseAbsencesListModal}
                className="text-white hover:bg-white/20 p-2.5 rounded-lg transition min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Corps Modal */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
              {eventAbsences.length === 0 ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-text-secondary font-semibold">Aucune demande d'absence</p>
                  <p className="text-sm text-text-muted mt-1">
                    Tous les participants sont présents
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {eventAbsences.map(absence => (
                    <div key={absence.id} className="border border-border-subtle rounded-lg p-4 hover:shadow-md transition">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-text-primary">{absence.employee_name}</p>
                          <p className="text-xs text-text-muted">
                            Signalé le {new Date(absence.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          absence.statut === 'en_attente' ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200' :
                          absence.statut === 'accepte' ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200' :
                          'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
                        }`}>
                          {absence.statut === 'en_attente' ? 'En attente' :
                           absence.statut === 'accepte' ? 'Acceptée' : 'Refusée'}
                        </span>
                      </div>

                      <div className="bg-elevated-1 rounded p-3 mb-3">
                        <p className="text-sm font-semibold text-text-secondary mb-1">Motif :</p>
                        <p className="text-sm text-text-secondary">{absence.motif}</p>
                      </div>

                      {absence.fichier_justificatif && (
                        <div className="mb-3">
                          <p className="text-sm font-semibold text-text-secondary mb-1">Justificatif :</p>
                          <a 
                            href={absence.fichier_justificatif} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                          >
                            <FileText className="w-4 h-4" />
                            Voir le document
                          </a>
                        </div>
                      )}

                      {absence.commentaire_admin && (
                        <div className="bg-blue-50 dark:bg-blue-950/40 rounded p-3 mb-3">
                          <p className="text-sm font-semibold text-blue-700 mb-1">Commentaire admin :</p>
                          <p className="text-sm text-blue-600 dark:text-blue-400">{absence.commentaire_admin}</p>
                          {absence.traite_par_name && (
                            <p className="text-xs text-blue-500 mt-1">
                              Traité par {absence.traite_par_name} le {new Date(absence.traite_le).toLocaleDateString('fr-FR')}
                            </p>
                          )}
                        </div>
                      )}

                      {absence.statut === 'en_attente' && (
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => {
                              const commentaire = window.prompt('Commentaire (optionnel) :');
                              if (commentaire !== null) {
                                handleProcessAbsence(absence.id, 'accepte', commentaire);
                              }
                            }}
                            className="flex-1 px-3 py-2 min-h-[44px] bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-semibold transition"
                          >
                            ✓ Accepter
                          </button>
                          <button
                            onClick={() => {
                              const commentaire = window.prompt('Motif du refus :');
                              if (commentaire !== null && commentaire.trim()) {
                                handleProcessAbsence(absence.id, 'refuse', commentaire);
                              } else if (commentaire !== null) {
                                alert('Veuillez indiquer un motif de refus');
                              }
                            }}
                            className="flex-1 px-3 py-2 min-h-[44px] bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold transition"
                          >
                            ✗ Refuser
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Footer sticky */}
            <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
              <button
                type="button"
                onClick={handleCloseAbsencesListModal}
                className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center border-2 border-border-strong text-text-secondary hover:bg-elevated-1 transition"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Signaler Absence (Employé) */}
      {showAbsenceModal && !isAdmin && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface shadow-2xl">
            {/* Barre de drag mobile */}
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            {/* Header Modal */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-gradient-to-r from-red-500 to-red-600 text-white shrink-0 sm:rounded-t-2xl">
              <div>
                <h3 className="text-lg sm:text-2xl font-bold flex items-center gap-2 sm:gap-3">
                  <AlertCircle className="w-5 h-5 sm:w-7 sm:h-7" />
                  Signaler une absence
                </h3>
                <p className="text-red-100 mt-1 text-xs sm:text-sm">
                  {selectedEvent.nom}
                </p>
              </div>
              <button
                onClick={handleCloseAbsenceModal}
                className="text-white hover:bg-white/20 p-2.5 rounded-lg transition min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Corps Modal */}
            <form onSubmit={handleSubmitAbsence} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
              <div className="bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Important :</strong> Signalez votre absence dès que possible. 
                  Un justificatif pourra vous être demandé.
                </p>
              </div>

              {/* Date événement */}
              <div className="bg-elevated-1 rounded-lg p-4">
                <div className="flex items-center gap-2 text-sm text-text-secondary mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="font-semibold">Date de l'événement</span>
                </div>
                <p className="text-text-primary font-semibold">
                  {formatDate(selectedEvent.date_debut)}
                </p>
                <p className="text-sm text-text-secondary mt-1">
                  {formatTime(selectedEvent.date_debut)} - {formatTime(selectedEvent.date_fin)}
                </p>
              </div>

              {/* Motif */}
              <div>
                <label className="block text-sm font-semibold text-text-secondary mb-2">
                  Motif de l'absence <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={absenceForm.motif}
                  onChange={(e) => setAbsenceForm({ ...absenceForm, motif: e.target.value })}
                  placeholder="Ex: Maladie, problème personnel, autre engagement..."
                  required
                  rows={4}
                  className="w-full px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                />
              </div>

              {/* Fichier justificatif (optionnel) */}
              <div>
                <label className="block text-sm font-semibold text-text-secondary mb-2">
                  Justificatif (optionnel)
                </label>
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-text-muted" />
                  <input
                    type="text"
                    value={absenceForm.fichier_justificatif}
                    onChange={(e) => setAbsenceForm({ ...absenceForm, fichier_justificatif: e.target.value })}
                    placeholder="URL du justificatif (certificat médical, etc.)"
                    className="flex-1 px-4 py-2.5 sm:py-2 min-h-[44px] text-base sm:text-sm border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <p className="text-xs text-text-muted mt-1">
                  Facultatif : lien vers un document (Google Drive, Dropbox, etc.)
                </p>
              </div>

            </div>
            {/* Footer sticky avec boutons */}
            <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
                <button
                  type="button"
                  onClick={handleCloseAbsenceModal}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center border-2 border-border-strong text-text-secondary hover:bg-elevated-1 transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-red-500 hover:bg-red-600 text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Envoi...' : 'Confirmer l\'absence'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
